import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Transaction } from '@/types';

export interface ReceiptPDFData {
  transaction: Transaction;
  client?: {
    name: string;
    email?: string;
    phone?: string;
  };
  receiptNumber: string;
  issueDate: Date;
  companyName: string;
  companyLogo?: string;
}

/**
 * Generate a professional receipt PDF
 */
export async function generateReceiptPDF(data: ReceiptPDFData): Promise<void> {
  try {
    const { transaction, client, receiptNumber, issueDate, companyName, companyLogo } = data;

    // Create new PDF document
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    // Get page dimensions
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;

    // Add company logo if available
    if (companyLogo) {
      try {
        // Fetch the logo image
        const logoImg = new Image();
        await new Promise((resolve, reject) => {
          logoImg.onload = resolve;
          logoImg.onerror = reject;
          logoImg.src = companyLogo;
        });

        // Add logo to PDF (centered, max 30mm width)
        const maxLogoWidth = 30;
        const maxLogoHeight = 15;
        const logoWidth = Math.min(maxLogoWidth, (logoImg.width * maxLogoHeight) / logoImg.height);
        const logoHeight = (logoImg.height * logoWidth) / logoImg.width;
        const logoX = (pageWidth - logoWidth) / 2;

        doc.addImage(logoImg, 'PNG', logoX, yPosition, logoWidth, logoHeight);
        yPosition += logoHeight + 10;
      } catch (error) {
        console.warn('Failed to add logo to PDF:', error);
        // Continue without logo if it fails
      }
    }

    // Add company name as header
    doc.setFontSize(20);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text(companyName, margin, yPosition);
    yPosition += 15;

    // Add receipt title
    doc.setFontSize(24);
    doc.setTextColor(0);
    doc.text('RECIBO', margin, yPosition);
    yPosition += 10;

    // Add receipt number and issue date
    doc.setFontSize(12);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text(`Nº ${receiptNumber}`, margin, yPosition);
    yPosition += 7;
    doc.text(
      `Emitido em: ${issueDate.toLocaleDateString('pt-BR')}`,
      margin,
      yPosition
    );
    yPosition += 15;

    // Add separator line
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 10;

    // Add payment status badge (simulated)
    doc.setFontSize(14);
    doc.setTextColor(0, 150, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('✓ PAGO', margin, yPosition);
    yPosition += 15;

    // Add transaction details section
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.setFont('helvetica', 'bold');
    doc.text('Detalhes da Transação', margin, yPosition);
    yPosition += 10;

    // Transaction info in two columns
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(100);

    // Left column
    let leftX = margin;
    let rightX = pageWidth / 2 + 10;

    // Description
    doc.text('Descrição:', leftX, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(transaction.description, leftX + 35, yPosition);
    yPosition += 8;

    // Amount
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Valor:', leftX, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0, 120, 80);
    doc.text(formatCurrency(transaction.amount), leftX + 35, yPosition);
    yPosition += 8;

    // Payment date
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Data do Recebimento:', leftX, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    const paymentDate = transaction.payment_date
      ? new Date(transaction.payment_date).toLocaleDateString('pt-BR')
      : new Date(transaction.date).toLocaleDateString('pt-BR');
    doc.text(paymentDate, leftX + 35, yPosition);
    yPosition += 8;

    // Payment method
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text('Forma de Pagamento:', leftX, yPosition);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(0);
    doc.text(transaction.payment_method || 'Não informado', leftX + 35, yPosition);
    yPosition += 8;

    // Due date (if exists)
    if (transaction.due_date) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text('Data de Vencimento:', leftX, yPosition);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(new Date(transaction.due_date).toLocaleDateString('pt-BR'), leftX + 35, yPosition);
      yPosition += 8;
    }

    // Category (if exists)
    if (transaction.category) {
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(100);
      doc.text('Categoria:', leftX, yPosition);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(transaction.category, leftX + 35, yPosition);
      yPosition += 8;
    }

    yPosition += 10;

    // Client information section
    if (client) {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'bold');
      doc.text('Dados do Cliente', margin, yPosition);
      yPosition += 10;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(11);
      doc.setTextColor(100);

      // Name
      doc.text('Nome:', leftX, yPosition);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(0);
      doc.text(client.name, leftX + 35, yPosition);
      yPosition += 8;

      // Email
      if (client.email) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('E-mail:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(client.email, leftX + 35, yPosition);
        yPosition += 8;
      }

      // Phone
      if (client.phone) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100);
        doc.text('Telefone:', leftX, yPosition);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(0);
        doc.text(client.phone, leftX + 35, yPosition);
        yPosition += 8;
      }

      yPosition += 10;
    }

    // Notes section (if exists)
    if (transaction.notes) {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.setFont('helvetica', 'bold');
      doc.text('Observações', margin, yPosition);
      yPosition += 8;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(60);

      // Word wrap for notes
      const lines = doc.splitTextToSize(transaction.notes, pageWidth - margin * 2);
      lines.forEach((line: string) => {
        doc.text(line, margin, yPosition);
        yPosition += 6;
      });

      yPosition += 10;
    }

    // Add separator line before footer
    doc.setDrawColor(200);
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 15;

    // Footer text
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'italic');
    const footerText = 'Este recibo confirma o pagamento efetuado conforme descrito acima.';
    const footerLines = doc.splitTextToSize(footerText, pageWidth - margin * 2);
    footerLines.forEach((line: string) => {
      doc.text(line, margin, yPosition);
      yPosition += 6;
    });

    yPosition += 10;

    // Company signature
    doc.setFont('helvetica', 'normal');
    doc.text(`${companyName} - ${issueDate.toLocaleDateString('pt-BR')}`, margin, yPosition);

    // Add signature area
    yPosition += 20;
    doc.setDrawColor(150);
    doc.line(margin, yPosition, pageWidth / 2 - margin, yPosition);
    yPosition += 5;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.setFont('helvetica', 'normal');
    doc.text('Assinatura do Recebedor', margin, yPosition);

    // Add page number in footer
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('Página 1 de 1', pageWidth / 2, pageHeight - 10, { align: 'center' });

    // Generate filename
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    const filename = `recibo_${receiptNumber}_${timestamp}.pdf`;

    // Save the PDF
    doc.save(filename);
  } catch (error) {
    console.error('Error generating receipt PDF:', error);
    throw new Error('Falha ao gerar o PDF do recibo. Por favor, tente novamente.');
  }
}

/**
 * Alternative method: Generate PDF from HTML template
 */
export async function generateReceiptPDFFromHTML(
  elementId: string,
  filename: string
): Promise<void> {
  try {
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error('Element not found for PDF generation');
    }

    // Create canvas from HTML element
    const canvas = await html2canvas(element, {
      scale: 2,
      backgroundColor: '#ffffff',
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    // Create PDF
    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight) * 200;
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 10;

    pdf.addImage(imgData, 'PNG', imgX, imgY, imgWidth * ratio, imgHeight * ratio);
    pdf.save(filename);
  } catch (error) {
    console.error('Error generating PDF from HTML:', error);
    throw new Error('Falha ao gerar o PDF. Por favor, tente novamente.');
  }
}

/**
 * Format currency value
 */
function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
}